import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { reportError } from '@/api/client';

/**
 * "Report a problem" button — drop it next to any error UI.
 *
 * After click it sends {screen, message, context} to the backend, which
 * DM's the admin via Telegram. Per-user rate-limited server-side (5/h).
 * The button auto-disables and turns into a "sent ✓" line on success.
 */

interface Props {
  /** Screen identifier — short, stable, e.g. "weekly", "reading", "master". */
  screen: string;
  /** The error text the user actually saw. Helps the admin reproduce. */
  message: string;
  /** Optional bag of context fields (language, action, etc.). Stringified server-side. */
  context?: Record<string, string | number | boolean | null>;
  className?: string;
}

export default function ErrorReportButton({
  screen, message, context, className,
}: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const send = async () => {
    if (state === 'sending' || state === 'sent') return;
    setState('sending');
    try {
      await reportError({ screen, message, context });
      setState('sent');
    } catch {
      setState('failed');
      // Allow retry after a short cool-down.
      setTimeout(() => setState('idle'), 4000);
    }
  };

  if (state === 'sent') {
    return (
      <p className={`text-xs italic text-gold-400/90 ${className ?? ''}`}>
        {t('error_report.sent')}
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={send}
      disabled={state === 'sending'}
      className={`text-xs italic text-gold-300/70 underline decoration-gold-500/30
                  underline-offset-2 transition hover:text-gold-200
                  hover:decoration-gold-400 disabled:opacity-50
                  ${className ?? ''}`}
    >
      {state === 'sending'
        ? t('error_report.sending')
        : state === 'failed'
          ? t('error_report.failed')
          : `📣 ${t('error_report.button')}`}
    </button>
  );
}
